/* ============================================================
   dtc-select.js — the site's own dropdown.

   WHY THIS EXISTS. A native <select> renders its list with the operating
   system, not the page: on Windows that is a white rectangle with a bright
   blue highlight and a 1px grey border, sitting inside a black-and-yellow site
   in a typeface nobody chose. Nothing about it can be styled — the list is
   drawn outside the document.

   HOW IT KEEPS WORKING. The native <select> is NEVER removed. It stays in the
   form, keeps its name, keeps its value, and remains both the thing that
   submits and the thing every existing change-handler listens to; this script
   only draws a button and a list on top of it, and writes the chosen value
   back with a real `change` event. With JavaScript unavailable, or if this
   file fails to load, the page is exactly what it was before: a working native
   select, visible and usable.

   Accessibility follows the ARIA button/listbox pattern: the button owns
   aria-haspopup, aria-expanded and aria-activedescendant, options are real
   options with aria-selected, and the keyboard does what a native select does
   — arrows, Home/End, type-ahead, Enter to commit, Escape to cancel.
   ============================================================ */
(function () {
  'use strict';

  var OPEN = 'is-open';
  var uid = 0;

  /* ONE outside-click listener for every instance, registered once. It used to
     be registered inside build(), which the Section Rendering swap re-runs for
     every widget in the replaced section - so each facet tick left another
     permanent listener on `document`, each closure holding a detached wrapper,
     its button, its list and its native select. Instances that have left the
     document are dropped here rather than tracked, so nothing has to be
     unregistered on teardown. */
  var live = [];
  document.addEventListener('click', function (e) {
    for (var i = live.length - 1; i >= 0; i--) {
      if (!live[i].wrap.isConnected) { live.splice(i, 1); continue; }
      if (!live[i].wrap.contains(e.target)) { live[i].close(false); }
    }
  });

  function build(select) {
    if (select.dataset.dtcSelectReady === '1') { return; }
    select.dataset.dtcSelectReady = '1';

    /* The real control stays in the form and stays clip-hidden - it submits,
       it fires change, and it is the whole fallback when the script does not
       run - but it must not be a FOCUS TARGET, or the shopper Tabs into a 1x1
       box they cannot see and arrow keys change a value with no feedback. */
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    var id = 'dtcsel-' + (++uid);
    var wrap = document.createElement('div');
    wrap.className = 'dtc-select';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dtc-select__btn';
    btn.id = id + '-btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    if (select.disabled) { btn.disabled = true; }

    /* The native select carries the accessible name (its <label for>), and that
       label must keep pointing at a real control, so the button borrows the
       name rather than stealing the label. */
    var label = select.labels && select.labels[0];
    if (label) {
      if (!label.id) { label.id = id + '-lab'; }
      btn.setAttribute('aria-labelledby', label.id + ' ' + id + '-val');
    }

    var val = document.createElement('span');
    val.className = 'dtc-select__value';
    val.id = id + '-val';
    btn.appendChild(val);

    var caret = document.createElement('span');
    caret.className = 'dtc-select__caret';
    caret.setAttribute('aria-hidden', 'true');
    btn.appendChild(caret);

    var list = document.createElement('ul');
    list.className = 'dtc-select__list';
    list.id = id + '-list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    btn.setAttribute('aria-controls', list.id);

    var options = [];

    function paint() {
      /* the select can be switched on and off after the button is built (a form's conditional questions) */
      btn.disabled = !!select.disabled;
      wrap.classList.toggle('is-disabled', !!select.disabled);
      list.textContent = '';
      options = [];
      Array.prototype.forEach.call(select.options, function (o, i) {
        var li = document.createElement('li');
        li.className = 'dtc-select__opt';
        li.id = id + '-o' + i;
        li.setAttribute('role', 'option');
        li.textContent = o.textContent.trim();
        li.setAttribute('aria-selected', o.selected ? 'true' : 'false');
        if (o.disabled) {
          li.setAttribute('aria-disabled', 'true');
          li.className += ' is-disabled';
        }
        li.addEventListener('click', function () {
          if (o.disabled) { return; }
          commit(i);
        });
        list.appendChild(li);
        options.push(li);
      });
      val.textContent = select.selectedIndex > -1
        ? select.options[select.selectedIndex].textContent.trim()
        : '';
      wrap.classList.toggle('is-empty', !val.textContent);
    }

    function activeIndex() {
      var cur = btn.getAttribute('aria-activedescendant');
      for (var i = 0; i < options.length; i++) { if (options[i].id === cur) { return i; } }
      return select.selectedIndex;
    }

    function activate(i) {
      if (i < 0 || i >= options.length) { return; }
      options.forEach(function (o) { o.classList.remove('is-active'); });
      options[i].classList.add('is-active');
      btn.setAttribute('aria-activedescendant', options[i].id);
      options[i].scrollIntoView({ block: 'nearest' });
    }

    function open() {
      if (!list.hidden) { return; }
      paint();
      list.hidden = false;
      wrap.classList.add(OPEN);
      btn.setAttribute('aria-expanded', 'true');
      /* Open where the list can be read whole. The room is measured inside every box that clips the control - a
         popup <dialog>, a scrolling panel - as well as the window, so a list in a popup never runs off its edge:
         downwards when it fits, upwards when only that side fits, otherwise the bigger side with a shorter list. */
      var r = btn.getBoundingClientRect();
      var top = 0, bottom = window.innerHeight;
      for (var el = wrap.parentElement; el && el !== document.body; el = el.parentElement) {
        if (el.tagName === 'DIALOG' || /(auto|scroll|hidden|clip)/.test(getComputedStyle(el).overflowY)) {
          var cr = el.getBoundingClientRect();
          top = Math.max(top, cr.top);
          bottom = Math.min(bottom, cr.bottom);
        }
      }
      var gap = 14;
      var need = Math.min(list.scrollHeight, 264) + gap;
      var below = bottom - r.bottom;
      var above = r.top - top;
      var up = below < need && (above >= need || above > below);
      wrap.classList.toggle('is-up', up);
      var room = (up ? above : below) - gap;
      list.style.maxHeight = room < 264 ? Math.max(120, Math.floor(room)) + 'px' : '';
      activate(select.selectedIndex);
    }

    function close(focusBtn) {
      if (list.hidden) { return; }
      list.hidden = true;
      list.style.maxHeight = '';
      wrap.classList.remove(OPEN, 'is-up');
      btn.setAttribute('aria-expanded', 'false');
      btn.removeAttribute('aria-activedescendant');
      if (focusBtn) { btn.focus(); }
    }

    function commit(i) {
      if (i < 0 || i >= select.options.length) { return; }
      if (select.options[i].disabled) { return; }
      if (select.selectedIndex !== i) {
        select.selectedIndex = i;
        /* The real events, bubbling, so every handler already listening to this
           select — variant switching, sort, conditional form fields — runs
           exactly as it did with the native control. */
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      paint();
      close(true);
    }

    /* type-ahead, like a native select */
    var typed = '';
    var typedAt = 0;
    function typeAhead(ch) {
      var now = (window.performance && performance.now) ? performance.now() : 0;
      typed = (now - typedAt < 900) ? typed + ch : ch;
      typedAt = now;
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].disabled) { continue; }
        if (select.options[i].textContent.trim().toLowerCase().indexOf(typed) === 0) {
          if (list.hidden) { commit(i); } else { activate(i); }
          return;
        }
      }
    }

    btn.addEventListener('click', function () {
      if (list.hidden) { open(); } else { close(true); }
    });

    btn.addEventListener('keydown', function (e) {
      var i = activeIndex();
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (list.hidden) { open(); } else { activate(Math.min(i + 1, options.length - 1)); }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (list.hidden) { open(); } else { activate(Math.max(i - 1, 0)); }
          break;
        case 'Home':
          if (!list.hidden) { e.preventDefault(); activate(0); }
          break;
        case 'End':
          if (!list.hidden) { e.preventDefault(); activate(options.length - 1); }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (list.hidden) { open(); } else { commit(i); }
          break;
        case 'Escape':
          if (!list.hidden) { e.preventDefault(); close(true); }
          break;
        case 'Tab':
          close(false);
          break;
        default:
          if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            typeAhead(e.key.toLowerCase());
          }
      }
    });

    live.push({ wrap: wrap, close: close });

    /* Something else changed the select (a variant picked elsewhere, a filter
       reset, a section swap): keep the button in step. */
    select.addEventListener('change', function () { paint(); });

    /* And something else REPLACED ITS OPTIONS. The cart's delivery estimator
       empties the region select and refills it when the country changes, and
       never dispatches `change` - so the button kept naming a region from the
       previous country while the form would have posted the new one. Watching
       the option list covers that and every other script that does the same,
       without either of them having to know about the other. */
    if (window.MutationObserver) {
      new MutationObserver(function () { paint(); })
        .observe(select, { childList: true, attributes: true, attributeFilter: ['disabled'] });
    }

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    wrap.appendChild(btn);
    wrap.appendChild(list);
    paint();
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(root.querySelectorAll('select[data-dtc-select]'), build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Sections re-render (collection filters, the theme editor), so re-scan. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  window.dtcSelectRefresh = init;

  /* Markup that arrives AFTER the first scan - a popup fetched on its first click (the gift card popup on product
     pages), a drawer, a swapped section - gets the same dropdown without its own refresh call, so no select on the
     site is ever left as the native control. build() is idempotent, so a select seen twice is built once. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) { continue; }
          if (node.matches && node.matches('select[data-dtc-select]')) { build(node); }
          else if (node.querySelector && node.querySelector('select[data-dtc-select]')) { init(node); }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
